import { Features } from '@weenat/client'
import {
  FeaturePriority,
  FeaturKey,
  getFeatureMetadata,
  getPriorityScore
} from '@weenat/client/dist/FeatureToggle'
import { canUser, ops } from '@weenat/client/dist/core/access'
import { useIntl } from '@weenat/wintl'
import { useOrgContext } from 'app/orgProvider'
import Modal, { ModalProps } from 'app/src/kit/Modal'
import { TogglePrimitive } from 'app/src/kit/fields/ToggleField'
import { useLoginAs, useToggleFeature } from 'app/state'
import { useUserContext } from 'app/userProvider'
import { capitalize } from 'app/utils'
import isNil from 'lodash-es/isNil'
import { styled, useTheme } from 'styled-components'
import Badge from '../kit/Badge'
import Link from '../kit/LinkComponent'
import MenuItem from '../kit/MenuItem'
import Text from '../kit/Text'

const PriorityIcon = ({ priority }: { priority: FeaturePriority }) => {
  return (
    <svg viewBox='0 0 24 24' width='24' height='24' fill='currentColor'>
      <rect x='4' y='14' width='3' height='6' rx='1' ry='1' fill={'green'} />
      <rect
        x='10'
        y='10'
        width='3'
        height='10'
        rx='1'
        ry='1'
        fill={priority === 'low' ? 'gray' : priority === 'medium' ? 'green' : 'green'}
      />
      <rect
        x='16'
        y='6'
        width='3'
        height='14'
        rx='1'
        ry='1'
        fill={priority === 'low' ? 'gray' : priority === 'medium' ? 'gray' : 'green'}
      />
    </svg>
  )
}

const FeatureFlagContainer = styled(MenuItem)`
  display: flex;
  flex-direction: row;
  padding: ${({ theme }) => theme.spacings.md}px 0px;
  gap: ${({ theme }) => theme.spacings.md}px;
  width: 100%;
  align-items: center;
`

const FeatureFlag = ({
  feature,
  isActive,
  onToggle
}: {
  feature: FeaturKey
  isActive: boolean
  onToggle: (value: boolean) => void
}) => {
  const metadata = getFeatureMetadata(feature)
  const { description, status, priority } = metadata

  return (
    <FeatureFlagContainer onPress={() => onToggle(!isActive)}>
      <Box $p='sm' $backgroundColor={'grayscale.100'} $borderRadius={'sm'}>
        <PriorityIcon priority={priority} />
      </Box>
      <Flex $flexDirection='column' $flex={1}>
        <Text $fontWeight='medium' $fontVariantNumeric='tabular-nums'>
          {capitalize(feature)}
        </Text>
        <Text $fontSize='sm'>{description}</Text>
      </Flex>
      <Badge
        $borderRadius={'sm'}
        $px='sm'
        $py={'sm'}
        $backgroundColor={'grayscale.black'}
        $color={'grayscale.white'}
      >
        {status}
      </Badge>
      <TogglePrimitive value={isActive} onToggle={onToggle} />
    </FeatureFlagContainer>
  )
}

const ActivateAllToggle = styled(TogglePrimitive)`
  padding-bottom: 16px;
  border-bottom: 1px solid ${(props) => props.theme.colors.grayscale[100]};
`

const FeatureToggleModal = ({ isOpen, close }: ModalProps) => {
  const { t } = useIntl()
  const { colors } = useTheme()
  const [features, setFeatures] = useToggleFeature()
  const { me } = useUserContext()
  const [loginAs] = useLoginAs()
  const org = useOrgContext()

  const areAllFeaturesActive = Object.entries(features).every(([, isActive]) => isActive)

  const canView = canUser(ops.view, 'debugMode', { account: me }) || !isNil(loginAs)

  const handleToggleAll = (value: boolean) => {
    const updatedFeatures: Record<string, boolean> = {}
    const newValue = !value

    Object.entries(features).forEach(([feature]) => {
      updatedFeatures[feature] = newValue
    })

    setFeatures((feats) => ({ ...feats, ...(updatedFeatures as Features) }))
  }

  if (!canView) return null

  const flags = Object.entries(features).sort((a, b) => {
    const featureA = a[0] as FeaturKey
    const featureB = b[0] as FeaturKey

    const priorityA = getFeatureMetadata(featureA).priority
    const priorityB = getFeatureMetadata(featureB).priority

    const aScore = getPriorityScore(priorityA)
    const bScore = getPriorityScore(priorityB)

    return bScore - aScore
  })

  return (
    <Modal isOpen={isOpen} close={close} title={t('titles.toggle_feature')} maxWidth={720}>
      <Box>
        <ActivateAllToggle
          label={t(
            areAllFeaturesActive ? 'toggle_features.disable_all' : 'toggle_features.enable_all'
          )}
          onToggle={handleToggleAll}
          value={areAllFeaturesActive}
          $backgroundColor={colors.feedback.success['500']}
          togglePosition='right'
        />
        <Box $py='md'>
          {flags.map(([feature, isActive]) => (
            <FeatureFlag
              key={feature}
              feature={feature}
              isActive={isActive}
              onToggle={(value: boolean) =>
                setFeatures((feats) => ({ ...feats, [feature]: !value }))
              }
            />
          ))}
        </Box>
        <Flex $p='lg' $mt='auto' $justifyContent='center'>
          <Link to={`/farms/${org.id}`} onPress={close} $underlined>
            {t('actions.go_home', { capitalize: true })}
          </Link>
        </Flex>
      </Box>
    </Modal>
  )
}

export default FeatureToggleModal
